import { nextTick, watch } from 'vue'
import { useUserStore } from '~/stores/user'
import { createLipSyncTracker } from '~/utils/lipsyncAnalytics'
import { readPricingProductIdsFromEnv } from '~/utils/pricingProductIdsEnv.js'
import { resolvePaymentCallbackPlanSelection } from '~/utils/paymentCallbackPlanSelection.js'
import {
  PAYMENT_SUCCESS_SESSION_STORAGE_KEY,
  persistPaymentReturnPending,
  readPaymentReturnPending,
  readPaymentSuccessSessionId,
  resolveAuthToken,
  stripPaymentQueryFromRoute,
} from '~/utils/paymentSuccessQuery'
import { readCheckoutPlanSelection } from '~/utils/checkoutPlanSelection.js'
import { invokeCheckoutPlanRestore } from '~/utils/checkoutPlanRestoreBus.js'
import {
  clearPaymentSuccessUrl,
  isPricingPagePath,
  readPaymentSuccessUrl,
} from '~/utils/paymentSuccessUrl.js'

let installed = false

function deferAfterPaint(task) {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void task()
      })
    })
    return
  }
  setTimeout(() => void task(), 0)
}

export default defineNuxtPlugin({
  name: 'payment-success-callback',
  dependsOn: ['pinia'],
  setup(nuxtApp) {
    if (!import.meta.client || installed) return
    installed = true

    const route = useRoute()
    const router = useRouter()
    const userStore = useUserStore()
    const config = useRuntimeConfig()
    const lipSyncLog = createLipSyncTracker(nuxtApp, userStore, route)
    const landingSearch = window.location.search

    let celebration = null
    const processingSessionIds = new Set()

    function translate(key) {
      return nuxtApp.$i18n?.t?.(key) ?? key
    }

    function resolveProductIds() {
      const fromConfig = config.public.pricingProductIds ?? {}
      if (Object.keys(fromConfig).length > 0) return fromConfig
      return readPricingProductIdsFromEnv(import.meta.env)
    }

    function resolveSelectionForCallback() {
      return resolvePaymentCallbackPlanSelection({
        route,
        productIds: resolveProductIds(),
      }) ?? readCheckoutPlanSelection()
    }

    function resolveIsPricingReturn() {
      const stored = readPaymentSuccessUrl()
      if (stored) {
        const path = stored.split('?')[0]
        return isPricingPagePath(path)
      }
      return isPricingPagePath(route.path)
    }

    function refreshUserCreditsInBackground() {
      const token = resolveAuthToken(userStore)
      if (!token) return
      void userStore.getUserInfo().catch((error) => {
        console.warn('payment success user refresh failed:', error)
      })
    }

    function showPaymentSuccessUi() {
      deferAfterPaint(async () => {
        const { AppMessage } = await import('~/utils/appMessage')
        AppMessage.success(translate('price.payment_success'))

        const { createConfettiCelebration } = await import('~/utils/confettiCelebration')
        celebration?.destroy()
        celebration = createConfettiCelebration()
        celebration.startCelebration(1500)
      })
    }

    function finalizePricingReturn() {
      stripPaymentQueryFromRoute(route, router)
      clearPaymentSuccessUrl()
    }

    async function runPostPaymentSuccessEffects({ isPricingReturn, selection }) {
      lipSyncLog.payStatus(true)

      if (isPricingReturn) {
        finalizePricingReturn()
      } else {
        userStore.setPriceModalShow(true)
        await nextTick()
      }

      void invokeCheckoutPlanRestore(selection)
      void nuxtApp.callHook('lip:payment-success', { selection })

      showPaymentSuccessUi()
      refreshUserCreditsInBackground()
    }

    async function processPaymentSuccess(sessionId) {
      if (processingSessionIds.has(sessionId)) return

      const isPricingReturn = resolveIsPricingReturn()

      try {
        if (sessionStorage.getItem(PAYMENT_SUCCESS_SESSION_STORAGE_KEY) === sessionId) {
          const selection =
            readPaymentReturnPending()?.selection
            ?? resolveSelectionForCallback()
          if (isPricingReturn) {
            finalizePricingReturn()
          } else {
            userStore.setPriceModalShow(true)
          }
          void invokeCheckoutPlanRestore(selection)
          void nuxtApp.callHook('lip:payment-success', { sessionId, selection })
          refreshUserCreditsInBackground()
          return
        }
      } catch {
        /* private mode */
      }

      processingSessionIds.add(sessionId)

      try {
        const selection = resolveSelectionForCallback()

        try {
          sessionStorage.setItem(PAYMENT_SUCCESS_SESSION_STORAGE_KEY, sessionId)
          persistPaymentReturnPending(sessionId, selection)
        } catch {
          /* ignore */
        }

        await runPostPaymentSuccessEffects({ isPricingReturn, selection })
      } catch (error) {
        console.error('payment success callback failed:', error)
        deferAfterPaint(async () => {
          const { AppMessage } = await import('~/utils/appMessage')
          AppMessage.error(translate('price.payment_failure'))
        })
        lipSyncLog.payStatus(false, String(error?.message || error))
      } finally {
        processingSessionIds.delete(sessionId)
      }
    }

    function tryHandlePaymentSuccess() {
      const sessionId = readPaymentSuccessSessionId(route, landingSearch)
      if (!sessionId) return
      void processPaymentSuccess(sessionId)
    }

    watch(
      () => [route.query.status, route.query.sessionId, route.query.session_id, route.query.product_id],
      () => {
        tryHandlePaymentSuccess()
      },
    )

    nuxtApp.hook('app:mounted', () => {
      void router.isReady().then(() => {
        deferAfterPaint(() => {
          tryHandlePaymentSuccess()
        })
      })
    })
  },
})
